import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import React, { useState } from 'react';

import {
  REPOST_CONFIRMATION_WARNING,
  checkPlatformMessage,
  stillBeingCheckedMessage,
} from '../../../../../../shared/provider-outcome';

import { Button } from '@/components/ui/button';
import { useDialogContentContainer } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getPlatformLabel } from '@/lib/platforms/identity';
import type { UnresolvedPublishTarget } from '@/services/data/PublishService';

interface RepostAfterCheckActionProps {
  targets: readonly UnresolvedPublishTarget[];
  isSending: boolean;
  /** Sends one platform again. A confirmation here says nothing about any other platform. */
  onConfirm: (platform: string) => void | Promise<void>;
}

const messageFor = (target: UnresolvedPublishTarget) =>
  target.deliveryStatus === 'native_status_unknown'
    ? stillBeingCheckedMessage(target.platform)
    : checkPlatformMessage(target.platform);

/**
 * Shown when Mora cannot tell whether a post went out. Each platform says its own state, and sending one again is the
 * author's call for that platform, in plain words.
 */
export const RepostAfterCheckAction: React.FC<RepostAfterCheckActionProps> = ({
  targets,
  isSending,
  onConfirm,
}) => {
  const [open, setOpen] = useState(false);
  const container = useDialogContentContainer();
  // A running check can still end in a live post, so only a target waiting on its author can be sent again.
  const available = targets.filter((target) => target.deliveryStatus === 'manual_action_required');
  const only = available.length === 1 ? available[0] : null;

  return (
    <div className="flex min-w-0 flex-1 basis-56 flex-wrap items-center justify-end gap-2">
      <div className="min-w-0 flex-1 basis-40 space-y-1">
        {targets.map((target) => (
          <p key={target.id} className="text-detail leading-snug text-muted-foreground">
            {messageFor(target)}
          </p>
        ))}
      </div>
      {available.length === 0 ? null : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="neutral" size="sm" disabled={isSending}>
              <ArrowClockwiseIcon className="size-3.5" weight="regular" />
              <span>
                {isSending
                  ? 'Sending...'
                  : only
                    ? `I checked ${getPlatformLabel(only.platform)}, post again`
                    : 'I checked, post again'}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" container={container} className="w-80 space-y-3 p-3">
            <div className="space-y-1">
              <p className="text-caption font-medium text-foreground">
                {only
                  ? `Post to ${getPlatformLabel(only.platform)} again?`
                  : 'Which one did you check?'}
              </p>
              <p className="text-detail leading-snug text-muted-foreground">
                {REPOST_CONFIRMATION_WARNING}
              </p>
            </div>
            <div className="space-y-1.5">
              {available.map((target) => (
                <Button
                  key={target.id}
                  variant="neutral"
                  size="sm"
                  className="w-full"
                  disabled={isSending}
                  onClick={() => {
                    setOpen(false);
                    void onConfirm(target.platform);
                  }}
                >
                  <ArrowClockwiseIcon className="size-3.5" weight="bold" />
                  {`Post to ${getPlatformLabel(target.platform)} again`}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
